import { Injectable, signal } from '@angular/core';
import { Configuration, ModulesScorecardsApplicationScoresRemoveScoresForRoundRemoveScoresForRoundResponse, ScorecardsService } from '../../api-client';
import { HttpClient } from '@angular/common/http';
import { LoggingService } from '../shared/logging/logging.service';
import { AdminSessionService } from '../admin-session/admin-session.service';
import {
  map,
  Observable,
  switchMap,
  tap,
} from 'rxjs';

import {
  ScoreResponse,
  ScorecardResponse,
  UpdateScoreRequest,
  CreateScoreRequest,
  UpdateScorecardRequest,
} from '../../models';
import { ShotFromResponse } from '../../models/shots/shotFromResponse';
import { ShotTypeResponse } from '../../models/shots/shotTypeResponse';

export interface ScorecardCounts {
  official: number;
  unofficial: number;
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class ScorecardService extends ScorecardsService {
  // Signals for current scorecard counts
  private _scorecardCounts = signal<ScorecardCounts>({
    official: 0,
    unofficial: 0,
    total: 0,
  });

  // Expose readonly signal
  readonly scorecardCounts = this._scorecardCounts.asReadonly();

  constructor(httpClient: HttpClient, config: Configuration, private loggingService: LoggingService, private adminSessionService: AdminSessionService) {
    super(httpClient, config.basePath!, config);
  }

  getScorecardCounts(roundId: string): Observable<ScorecardCounts> {
    // Otherwise, fetch filtered data
    this.loggingService.debug('ScorecardService',
      `Fetching scorecard counts for roundId: ${roundId}`
    );
    return this.getByRound(roundId).pipe(
      map((scorecards) => {
        const counts = this.calculateCounts(scorecards);

        this._scorecardCounts.set(counts);

        return counts;
      })
    );
  }

  private calculateCounts(scorecards: ScorecardResponse[]): ScorecardCounts {
    const officialCount = scorecards.filter((s) => s.official).length;
    const total = scorecards.length;

    return {
      official: officialCount,
      unofficial: total - officialCount,
      total,
    };
  }

  refreshScorecardCounts(roundId: string): void {
    this.getScorecardCounts(roundId).subscribe();
  }

  getByRound(roundId: string | undefined): Observable<ScorecardResponse[]> {
    const tournamentId = undefined;
    return super.getScorecardsEndpoint(tournamentId, roundId) as Observable<
      ScorecardResponse[]
    >;
  }

  markOfficial(scorecardId: string): Observable<ScorecardResponse> {
    const official = true;
    const officialOnUtc = new Date();

    return super
      .scorecardsScorecardIdMarkOfficialPut(scorecardId, {
        official,
        officialOnUtc,
      })
      .pipe(
        switchMap(() => {
          // Fetch the updated scorecard
          return super.getScorecardByIdEndpoint(scorecardId);
        }),
        tap((scorecard) => {
          this.refreshScorecardCounts(scorecard?.roundId!);
        })
      );
  }

  markNotOfficial(scorecardId: string): Observable<ScorecardResponse> {
    const official = false;
    return super
      .scorecardsScorecardIdMarkOfficialPut(scorecardId, {
        official,
      })
      .pipe(
        switchMap(() => {
          return super.getScorecardByIdEndpoint(scorecardId);
        }),
        tap((scorecard) => {
          // Refresh counts asynchronously
          this.refreshScorecardCounts(scorecard?.roundId!);
        })
      );
  }

  getScores(scorecardId: string): Observable<ScoreResponse[]> {
    return super.getScoresByScorecardEndpoint(scorecardId);
  }

  updateScorecard(
    scorecard: UpdateScorecardRequest
  ): Observable<ScorecardResponse> {
    return super
      .scorecardsScorecardIdPut(scorecard.scorecardId!, scorecard)
      .pipe(
        switchMap(() => {
          return super.getScorecardByIdEndpoint(scorecard.scorecardId!);
        }),
        tap((scorecard) => {
          if (scorecard?.roundId) {
            this.refreshScorecardCounts(scorecard.roundId);
          }
        })
      );
  }

  createScore(score: ScoreResponse): Observable<ScorecardResponse> {
    const scorecardId = score.scorecardId!;
    const adminIdentifier = this.adminSessionService.getAdminIdentifier();
    const newScore: CreateScoreRequest = {
      ...score,
      shots: score.shots?.map((shot) => ({
        ...shot,
        submittedAt: shot.submittedAt
          ? new Date(shot.submittedAt).toLocaleString()
          : new Date().toLocaleString(),
      })),
      submittedAt: new Date(),
      submittedBy: adminIdentifier, // Use unique admin identifier for filtering
    };

    return this.scorecardsScorecardIdScoresPost(scorecardId, newScore).pipe(
      switchMap(() => {
        return this.getScores(scorecardId);
      }),
      switchMap(() => {
        return this.getScorecardByIdEndpoint(scorecardId);
      })
    );
  }

  /**
   * Creates a score and returns the newly created scoreId (GUID as string).
   * This method is intended for internal use where the caller needs the ID immediately
   * (e.g., buffering logic that may subsequently delete/update the same hole).
   */
  createScoreReturningId(score: ScoreResponse): Observable<string> {
    const scorecardId = score.scorecardId!;
    const adminIdentifier = this.adminSessionService.getAdminIdentifier();
    const newScore: CreateScoreRequest = {
      ...score,
      shots: score.shots?.map((shot) => ({
        ...shot,
        submittedAt: shot.submittedAt
          ? new Date(shot.submittedAt).toLocaleString()
          : new Date().toLocaleString(),
      })),
      submittedAt: new Date(),
      submittedBy: adminIdentifier, // Use unique admin identifier for filtering
    };

    // Directly return the server-assigned scoreId (string body)
    return this.scorecardsScorecardIdScoresPost(scorecardId, newScore);
  }


  deleteScore(
    scorecardId: string,
    scoreId: string
  ): Observable<ScorecardResponse> {
    return this.scorecardsScorecardIdScoresScoreIdDelete(
      scorecardId,
      scoreId
    ).pipe(
      switchMap(() => {
        return this.getScorecardByIdEndpoint(scorecardId);
      })
    );
  }

  updateScore(score: ScoreResponse): Observable<ScorecardResponse> {
    const scorecardId = score.scorecardId!;
    const adminIdentifier = this.adminSessionService.getAdminIdentifier();
    const modifiedScore: UpdateScoreRequest = {
      ...score,
      shots: score.shots?.map((shot) => ({
        ...shot,
        submittedAt: shot.submittedAt
          ? new Date(shot.submittedAt).toLocaleString()
          : new Date().toLocaleString(),
      })),
      submittedAt: new Date(),
      submittedBy: adminIdentifier, // Use unique admin identifier for filtering
    };

    return this.scorecardsScorecardIdScoresScoreIdPut(
      scorecardId,
      modifiedScore.scoreId!,
      modifiedScore
    ).pipe(
      switchMap(() => {
        return this.getScorecardByIdEndpoint(scorecardId);
      })
    );
  }

  /**
   * Get scorecard by ID from API
   */
  getScorecardById(scorecardId: string): Observable<ScorecardResponse> {
    return super.getScorecardByIdEndpoint(scorecardId);
  }

  getScoreById(
    scorecardId: string,
    scoreId: string
  ): Observable<ScoreResponse> {
    return super.getScoreByIdEndpoint(scorecardId, scoreId);
  }

  getShotFroms(): Observable<ShotFromResponse[]> {
    return super.getShotFromsEndpoint().pipe(
      map(shotFroms => shotFroms.map(shot => ({
        ...shot,
        id: shot.shotFromId
      })))
    );
  }

  getShotTypes(): Observable<ShotTypeResponse[]> {
    return super.getShotTypesEndpoint().pipe(
      map(shotTypes => shotTypes.map(type => ({
        ...type,
        id: type.shotTypeId
      })))
    );
  }

  removeScoresForRound(
    tournamentId: string,
    roundId: string
  ): Observable<ModulesScorecardsApplicationScoresRemoveScoresForRoundRemoveScoresForRoundResponse> {
    this.loggingService.debug(
      'ScorecardService',
      `Removing scores for tournamentId: ${tournamentId}, roundId: ${roundId}`
    );

    return super.tournamentsTournamentIdRoundsRoundIdScoresDelete(
      tournamentId,
      roundId
    );
  }
}
